import React, { useState } from "react";

export default function LawSourcePanel({ lawReferences, precedentReferences, category, lawApiStatus }) {
  const hasLaws = lawReferences && lawReferences.length > 0;
  const hasPrecs = precedentReferences && precedentReferences.length > 0;
  const hasData = hasLaws || hasPrecs;

  if (!hasData) {
    let icon = "\u2696\uFE0F";
    let title = "\uAD00\uB828 \uBC95\uB839 \uC6D0\uBB38\uC774";
    let subtitle = "\uC774\uACF3\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.";

    if (lawApiStatus === "no_api_key") {
      icon = "\uD83D\uDD11";
      title = "\uBC95\uB839 API \uC124\uC815 \uD544\uC694";
      subtitle = "LAW_API_OC \uD658\uACBD\uBCC0\uC218\uB97C \uC124\uC815\uD574\uC8FC\uC138\uC694.";
    } else if (lawApiStatus === "api_error") {
      icon = "\u26A0\uFE0F";
      title = "\uBC95\uB839 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4";
      subtitle = "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.";
    } else if (lawApiStatus === "no_results") {
      icon = "\uD83D\uDD0D";
      title = "\uAD00\uB828 \uBC95\uB839\uC744 \uCC3E\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4";
      subtitle = "\uB2E4\uB978 \uD0A4\uC6CC\uB4DC\uB85C \uC9C8\uBB38\uD574\uBCF4\uC138\uC694.";
    }

    return (
      <div className="h-full flex items-center justify-center p-6 text-gray-400 text-sm bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
        <div className="text-center">
          <p className="text-3xl mb-3">{icon}</p>
          <p className="font-medium">{title}</p>
          <p>{subtitle}</p>
        </div>
      </div>
    );
  }

  const color = category === "TAX" ? "emerald" : "blue";

  return (
    <div className="h-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-0">

        {/* ===== 관련 법령 섹션 ===== */}
        {hasLaws && (
          <div>
            {/* 법령 섹션 헤더 */}
            <div className={`px-4 py-2.5 bg-${color}-50 rounded-t-lg border border-${color}-100 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{"\uD83D\uDCDC"}</span>
                <h3 className={`font-bold text-base text-${color}-800`}>{"\uAD00\uB828 \uBC95\uB839"}</h3>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold bg-white text-${color}-600 border border-${color}-200`}>
                {lawReferences.length}{"\uAC74"}
              </span>
            </div>

            {/* 법령 목록 */}
            <div className={`border-x border-b border-${color}-100 rounded-b-lg divide-y divide-${color}-50`}>
              {lawReferences.map((law, idx) => (
                <div key={idx} className="px-4 py-2.5">
                  <a
                    href={law.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`flex-shrink-0 w-2 h-2 rounded-full bg-${color}-500`}></span>
                      <span className="font-bold text-gray-700 break-keep leading-tight text-[13px] group-hover:text-[#725031]">
                        {law.name}
                      </span>
                    </div>
                    <svg className={`w-4 h-4 flex-shrink-0 ml-3 text-${color}-400 group-hover:text-${color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>

                  {/* 조문별 링크 칩 */}
                  {law.articles && law.articles.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1 ml-4">
                      {law.articles.map((article, aIdx) => (
                        <a
                          key={aIdx}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 text-xs font-medium text-${color}-700 bg-white px-2 py-0.5 rounded-md border border-${color}-200 hover:bg-${color}-100 hover:shadow-sm transition-all`}
                        >
                          {article.number.includes('\uC870') ? `\uC81C${article.number}` : `\uC81C${article.number}\uC870`}
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== 관련 판례 섹션 ===== */}
        {hasPrecs && (
          <div className={hasLaws ? "mt-2" : ""}>
            {/* 판례 섹션 헤더 */}
            <div className="px-4 py-2.5 bg-purple-50 rounded-t-lg border border-purple-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{"\u2696\uFE0F"}</span>
                <h3 className="font-bold text-base text-purple-800">{"\uAD00\uB828 \uD310\uB840"}</h3>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-white text-purple-600 border border-purple-200">
                {precedentReferences.length}{"\uAC74"}
              </span>
            </div>

            {/* 판례 카드 목록 */}
            <div className="border-x border-b border-purple-100 rounded-b-lg divide-y divide-purple-50">
              {precedentReferences.map((prec, pIdx) => (
                <PrecedentCard key={pIdx} prec={prec} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/** 판례 카드 컴포넌트 - 법원명, 선고일, 사건종류, 판결요지/판시사항 포함 */
function PrecedentCard({ prec }) {
  const [expanded, setExpanded] = useState(true);  // 기본값을 true로 변경 (항상 펼쳐진 상태)
  const hasSummary = prec.summary && prec.summary.length > 0;
  const hasKeyIssues = prec.key_issues && prec.key_issues.length > 0;
  const hasDetails = hasSummary || hasKeyIssues;

  return (
    <div className="bg-white hover:bg-purple-50/30 transition-colors">
      {/* 메인 링크 영역 */}
      <a
        href={prec.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block px-4 py-2.5 group"
      >
        <div className="font-bold text-gray-700 break-keep group-hover:text-purple-700 mb-1.5 text-[13px] leading-snug">
          {prec.case_name || "\uD310\uB840 \uC815\uBCF4"}
        </div>

        {/* 메타 정보 배지들 */}
        <div className="flex flex-wrap gap-1">
          {prec.case_number && (
            <span className="text-xs text-gray-500 font-medium bg-gray-100 inline-block px-1.5 py-0.5 rounded border border-gray-200">
              {prec.case_number}
            </span>
          )}
          {prec.court_name && (
            <span className="text-xs text-purple-600 font-medium bg-purple-50 inline-block px-1.5 py-0.5 rounded border border-purple-200">
              {prec.court_name}
            </span>
          )}
          {prec.judgment_date && (
            <span className="text-xs text-gray-500 font-medium bg-gray-50 inline-block px-1.5 py-0.5 rounded border border-gray-200">
              {prec.judgment_date}
            </span>
          )}
          {prec.case_type && (
            <span className="text-xs text-purple-600 font-medium bg-purple-50 inline-block px-1.5 py-0.5 rounded border border-purple-200">
              {prec.case_type}
            </span>
          )}
        </div>
      </a>

      {/* 판결요지/판시사항 토글 */}
      {hasDetails && (
        <div className="border-t border-purple-50">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors"
          >
            <span className="flex items-center gap-1">
              {hasSummary && <span>{"\uD310\uACB0\uC694\uC9C0"}</span>}
              {hasKeyIssues && <span>{hasSummary ? " · " : ""}{"\uD310\uC2DC\uC0AC\uD56D"}</span>}
            </span>
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expanded && (
            <div className="px-4 pb-2.5 space-y-2">
              {hasSummary && (
                <div>
                  <div className="text-xs font-semibold text-purple-700 mb-0.5">{"\uD310\uACB0\uC694\uC9C0"}</div>
                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap bg-purple-50/60 rounded-lg p-2.5 border border-purple-100">
                    {prec.summary}
                  </p>
                </div>
              )}
              {hasKeyIssues && (
                <div>
                  <div className="text-xs font-semibold text-purple-700 mb-0.5">{"\uD310\uC2DC\uC0AC\uD56D"}</div>
                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap bg-purple-50/60 rounded-lg p-2.5 border border-purple-100">
                    {prec.key_issues}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
